import React from 'react';
import '../LandingPage.css';
import mbIcon from '../../assets/mb_icon.png';

interface EducationManagementSystemProps {
  onBack: () => void;
  onNavigateToLogin: () => void;
  onNavigateToContact: () => void;
}

const EducationManagementSystem: React.FC<EducationManagementSystemProps> = ({ onBack, onNavigateToLogin, onNavigateToContact }) => {
  const modules = [
    { icon: 'bi-mortarboard', title: 'Academic Management', desc: 'Curriculum planning, lesson schedules, exam management, and grade tracking', color: '#5C2597' },
    { icon: 'bi-people', title: 'Student Information System', desc: 'Complete student profiles, enrollment, transfers, and academic history', color: '#2D7DD2' },
    { icon: 'bi-person-workspace', title: 'Staff & HR Management', desc: 'Teacher profiles, attendance, payroll, leave management, and performance', color: '#28A745' },
    { icon: 'bi-currency-rupee', title: 'Financial Management', desc: 'Fee collection, invoicing, expense tracking, and financial reporting', color: '#FFC107' },
    { icon: 'bi-chat-dots', title: 'Communication Hub', desc: 'Parent-teacher messaging, announcements, and WhatsApp integration', color: '#25D366' },
    { icon: 'bi-graph-up', title: 'Analytics & Reports', desc: 'Real-time dashboards, performance analytics, and custom reports', color: '#E91E63' },
  ];

  const benefits = [
    { title: 'Centralized Data', desc: 'All school data in one secure platform' },
    { title: 'Time Savings', desc: 'Automate repetitive administrative tasks' },
    { title: 'Better Decisions', desc: 'Data-driven insights for school improvement' },
    { title: 'Parent Engagement', desc: 'Keep parents informed and involved' },
  ];

  return (
    <div className="landing-page min-vh-100">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm">
        <div className="container-fluid px-4">
          <a className="navbar-brand d-flex align-items-center" href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <img src={mbIcon} alt="My Learning Board" className="mb-logo" />
            <span className="ms-2 fw-bold text-primary-custom">My Learning Board</span>
          </a>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary-custom" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Home
            </button>
            <button className="btn btn-primary-custom text-white" onClick={onNavigateToLogin}>
              Login
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4">
          <div className="row align-items-center py-5">
            <div className="col-lg-8 mx-auto text-center">
              <span className="badge bg-white text-primary mb-3 px-3 py-2">
                <i className="bi bi-lightning-charge me-2"></i>Complete Education Solution
              </span>
              <h1 className="display-4 fw-bold mb-4">
                All-in-One Education Management System
              </h1>
              <p className="lead mb-4 opacity-90">
                Streamline every aspect of school administration with our comprehensive
                education management platform. From admissions to alumni - we've got you covered.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <button className="btn btn-light btn-lg px-4" onClick={onNavigateToContact}>
                  <i className="bi bi-calendar-event me-2"></i>Schedule Demo
                </button>
                <button className="btn btn-outline-light btn-lg px-4" onClick={onNavigateToLogin}>
                  <i className="bi bi-box-arrow-in-right me-2"></i>Login
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section className="py-5">
        <div className="container-fluid px-4">
          <div className="text-center mb-5">
            <h2 className="fw-bold mb-3">Complete Education Management Modules</h2>
            <p className="text-muted lead">Everything you need to run a modern educational institution</p>
          </div>
          <div className="row g-4">
            {modules.map((module, idx) => (
              <div key={idx} className="col-md-6 col-lg-4">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="rounded d-inline-flex align-items-center justify-content-center mb-3"
                      style={{ width: '60px', height: '60px', backgroundColor: `${module.color}15` }}>
                      <i className={`bi ${module.icon} fs-4`} style={{ color: module.color }}></i>
                    </div>
                    <h5 className="card-title">{module.title}</h5>
                    <p className="card-text text-muted">{module.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-5 bg-light">
        <div className="container-fluid px-4">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="fw-bold mb-4">Why Choose Our Education Management System?</h2>
              <p className="text-muted mb-4">
                Our platform is designed by educators, for educators. We understand the
                challenges schools face and have built solutions that actually work.
              </p>
              <div className="row g-3">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="col-6">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                      <div>
                        <strong>{benefit.title}</strong>
                        <p className="small text-muted mb-0">{benefit.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-6">
              <div className="bg-white rounded-4 p-4 shadow">
                <h5 className="mb-4"><i className="bi bi-bar-chart me-2"></i>Impact Statistics</h5>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Administrative Time Saved</span>
                    <strong>70%</strong>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div className="progress-bar bg-success" style={{ width: '70%' }}></div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Parent Satisfaction</span>
                    <strong>95%</strong>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div className="progress-bar bg-primary" style={{ width: '95%' }}></div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span>Fee Collection Rate</span>
                    <strong>98%</strong>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div className="progress-bar bg-warning" style={{ width: '98%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container-fluid px-4 text-center">
          <h2 className="mb-3">Ready to Transform Your School's Management?</h2>
          <p className="lead opacity-75 mb-4">Join thousands of schools already using our education management system</p>
          <button className="btn btn-light btn-lg px-5" onClick={onNavigateToContact}>
            <i className="bi bi-rocket-takeoff me-2"></i>Get Started Today
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer py-4">
        <div className="container-fluid px-4 text-center">
          <p className="mb-0 small">&copy; 2026 Omnix Digital Solutions. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default EducationManagementSystem;
